import { observer } from "mobx-react-lite";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useServerTable } from "@/hooks/useServerTable";
import { Columns } from "./Columns";
import { event } from "@/endpoints/dryp/event";
import { CUSTOM_FILTERS } from "@/config/list";
import { EventTypeFilter } from "./EventTypeFilter";
import { getTableStructureFilterParams } from "@/utils/filters";
import { StructureFilters } from "@/components/filters/StructureFilters";
import { SearchInput } from "@/components/SearchInput";
import { DateRangePicker, TimePeriodOption } from "@/components/DateRangePicker";
import { List } from "@/components/List";
import { ListToolbarActions } from "@/components/List/ListToolbar/ListToolbarActions";
import { useTableFilters } from "@/hooks/useTableFilters";
import { DateRange } from "react-day-picker";
import { DateTime } from "luxon";

const EVENT_TIME_PERIODS: TimePeriodOption[] = [
	{ label: "24 hours", value: "last24h" },
	{ label: "7 days", value: "last7d" },
	{ label: "14 days", value: "last14d" },
	{ label: "30 days", value: "last30d" },
	{ label: "90 days", value: "last90d" },
	{ label: "1 year", value: "last1y" },
];

const toISOString = (date: Date): string => DateTime.fromJSDate(date).toUTC().toISO()!;

const getDefault7dRange = () => {
	const now = DateTime.now();
	return {
		from: now.minus({ days: 7 }).toUTC().toISO()!,
		to: now.toUTC().toISO()!,
	};
};

export const Events = observer(() => {
	const eventsApi = useAsyncStateStore(event.getEvents, { debounce: 300 });

	const defaultRange = getDefault7dRange();

	const { table } = useServerTable({
		columns: Columns,
		data: eventsApi.data?.items || [],
		fetchFn: params => {
			const structureFilters = getTableStructureFilterParams(params.filters);
			const eventTypesRaw = params.filters?.find(f => f.id === CUSTOM_FILTERS.eventType)?.value;
			const eventTypes = Array.isArray(eventTypesRaw)
				? eventTypesRaw.map(Number)
				: eventTypesRaw != null
					? [Number(eventTypesRaw)]
					: undefined;
			const from =
				(params.filters?.find(f => f.id === CUSTOM_FILTERS.from)?.value as string | undefined) ??
				defaultRange.from;
			const to =
				(params.filters?.find(f => f.id === CUSTOM_FILTERS.to)?.value as string | undefined) ?? defaultRange.to;
			return eventsApi.fetch(
				{ pageNumber: params.page, pageSize: params.pageSize },
				params.sort,
				structureFilters,
				eventTypes,
				from,
				to,
			);
		},
		initialValues: {
			page: 0,
			pageSize: 10,
			columnVisibility: {
				structureId: false,
				isEventDataValidated: false,
			},
		},
		enableUrlParams: true,
		initFromUrl: true,
	});

	const filterApi = useTableFilters(table);

	const fromValue = filterApi.getFilter(CUSTOM_FILTERS.from) as string | undefined;
	const toValue = filterApi.getFilter(CUSTOM_FILTERS.to) as string | undefined;

	const dateRange: DateRange | undefined =
		fromValue || toValue
			? {
					from: fromValue ? DateTime.fromISO(fromValue).toJSDate() : undefined,
					to: toValue ? DateTime.fromISO(toValue).toJSDate() : undefined,
				}
			: {
					from: DateTime.fromISO(defaultRange.from).toJSDate(),
					to: DateTime.fromISO(defaultRange.to).toJSDate(),
				};

	const onDateRangeChange = (range: DateRange | undefined) => {
		if (range?.from) {
			filterApi.setFilter(CUSTOM_FILTERS.from, toISOString(range.from));
		} else {
			filterApi.clearFilter(CUSTOM_FILTERS.from);
		}
		if (range?.to) {
			filterApi.setFilter(CUSTOM_FILTERS.to, toISOString(range.to));
		} else {
			filterApi.clearFilter(CUSTOM_FILTERS.to);
		}
	};

	return (
		<div className="flex flex-col gap-4 md:gap-6 p-4 md:p-6 min-h-full overflow-hidden">
			<header className="flex items-center justify-between">
				<h1 className="text-2xl md:text-4xl font-semibold tracking-tight">Events</h1>
			</header>

			<div className="flex flex-wrap md:flex-nowrap items-end py-4 gap-2 md:gap-4">
				<SearchInput
					value={(filterApi.getFilter(CUSTOM_FILTERS.search) as string | undefined) ?? ""}
					onChange={value => filterApi.setFilter(CUSTOM_FILTERS.search, value)}
				/>
				<EventTypeFilter filters={filterApi} />
				<div className="flex flex-col space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Select Period</span>
					<DateRangePicker
						date={dateRange}
						onChange={onDateRangeChange}
						onClose={onDateRangeChange}
						timePeriods={EVENT_TIME_PERIODS}
					/>
				</div>
				<StructureFilters filter={filterApi} hideNetworkType hideTypes />
				<ListToolbarActions table={table} loading={eventsApi.loading} />
			</div>

			<List
				table={table}
				pagination={{
					total: eventsApi.data?.query?.pagination.totalItems || 0,
				}}
				layout={{
					loading: eventsApi.loading,
				}}
				toolbar={{ hideToolbar: true }}
			/>
		</div>
	);
});
